import type {
  BaseEntry,
  BoardEntry,
  BusinessPermitEntry,
  CompetencyEntry,
  DeedEntry,
  OfficeEntry,
  PicEntry,
  RawEntry,
  TaxDocEntry,
} from '../models/panel-entry.model';
import {
  normalizeBoard,
  normalizeBusinessPermit,
  normalizeCompetency,
  normalizeDeed,
  normalizeOffice,
  normalizePic,
  normalizeTaxDoc,
} from '../utils/panel-normalizers.util';
import {
  MIN_ENTRY,
  isSimpleOrg,
  requiredBoardTypes,
  requiredBusinessPermits,
  requiredDeedTypes,
  requiredTaxTypes,
  type Vendor,
} from './panel-rules';

/**
 * Registry panel profil vendor — **deklaratif, satu array, tanpa `switch`**.
 *
 * Semua validator menerima array yang SUDAH ternormalisasi dan SUDAH di-resolve. Karena itu
 * validator di sini:
 * - tidak pernah memanggil `JSON.parse` (tugas normalizer),
 * - tidak pernah menyentuh `revision_items` (tugas `resolveEntities`),
 * - tidak pernah memfilter `entryType === 'DELETE'` (sudah dibuang di resolve),
 * - selalu mengembalikan `boolean` eksplisit.
 *
 * Lihat plans/KONSEP-PANEL-VALIDATION.md §6–§7 dan plans/vendors/profile/PLAN-PROFILE-TAB.md §9.
 */

/** Urutan = nomor menu kanonik 1–14 (PLAN-PROFILE-TAB §9a). */
export type PanelKey =
  | 'GENERAL_DATA' // 1  Umum
  | 'LEGAL_BASIS' // 2  Akta
  | 'SHAREHOLDER' // 3  Pemegang Saham          — belum berkontrak
  | 'BOARD' // 4  Susunan Pengurus
  | 'BUSINESS_PERMIT' // 5  Izin Usaha
  | 'PERSON_IN_CHARGE' // 6  Penanggung Jawab (PIC)
  | 'TOOLS' // 7  Alat                    — belum berkontrak
  | 'EXPERTS' // 8  Tenaga Ahli             — belum berkontrak
  | 'CERTIFICATION' // 9  Sertifikasi             — belum berkontrak
  | 'COMPETENCY' // 10 Kompetensi
  | 'EXPERIENCE' // 11 Pengalaman Kerja        — belum berkontrak
  | 'BANK_ACCOUNT' // 12 Akun Bank               — belum berkontrak
  | 'FINANCIAL_STATEMENT' // 13 Laporan Keuangan       — belum berkontrak
  | 'TAX_DOCUMENT'; // 14 Dokumen Pajak

export type PanelGroup = 'ADMINISTRASI' | 'KOMPETENSI' | 'KEUANGAN';

/**
 * Tipe entri per panel. Ini yang membuat validator terketik penuh — `ctx.data.LEGAL_BASIS`
 * langsung bertipe `readonly DeedEntry[]`, tanpa satu pun `as` di badan validator.
 *
 * Panel yang belum berkontrak memakai `BaseEntry`: arraynya memang selalu kosong.
 */
export interface PanelEntryMap {
  GENERAL_DATA: OfficeEntry;
  LEGAL_BASIS: DeedEntry;
  SHAREHOLDER: BaseEntry;
  BOARD: BoardEntry;
  BUSINESS_PERMIT: BusinessPermitEntry;
  PERSON_IN_CHARGE: PicEntry;
  TOOLS: BaseEntry;
  EXPERTS: BaseEntry;
  CERTIFICATION: BaseEntry;
  COMPETENCY: CompetencyEntry;
  EXPERIENCE: BaseEntry;
  BANK_ACCOUNT: BaseEntry;
  FINANCIAL_STATEMENT: BaseEntry;
  TAX_DOCUMENT: TaxDocEntry;
}

/** Data semua panel — sudah normalized + resolved. `data[KEY]` DIJAMIN array (lihat `EMPTY_PANEL_DATA`). */
export type PanelData = { readonly [K in PanelKey]: readonly PanelEntryMap[K][] };

export interface Ctx {
  vendor: Vendor;
  data: PanelData;
}

/**
 * Apa yang kurang dari sebuah panel — DATA, bukan kalimat jadi.
 *
 * Konsep mengembalikan `string[]` berisi kalimat Indonesia; itu tidak bisa diterjemahkan.
 * Template yang merangkainya jadi teks (PLAN-PROFILE-TAB §D11).
 */
export type MissingHint =
  { kind: 'IDENT'; value: string } | { kind: 'NO_ATTACHMENT'; count: number };

/**
 * Satu butir checklist section — DATA, bukan kalimat jadi (alasan yang sama dengan `MissingHint`).
 *
 * `IDENT` = satu jenis dokumen wajib (`'TDP'`, `'NPWP'`, …); `RULE` = satu syarat yang bukan jenis
 * dokumen, dengan key i18n LITERAL.
 */
export type ChecklistItem =
  | { kind: 'IDENT'; value: string; done: boolean }
  | { kind: 'RULE'; labelKey: string; done: boolean };

export interface PanelDef {
  key: PanelKey;
  /** Key i18n, BUKAN string bahasa — app ini dwibahasa (PLAN-PROFILE-TAB §D10). */
  labelKey: string;
  /** Pesan saat panel tidak valid. `null` untuk panel yang belum berkontrak. */
  messageKey: string | null;
  /**
   * **Slug section, TELANJANG** — `'organizations'`, BUKAN `'/vendor/profile/organizations'`.
   *
   * `profile-tab.html` memakainya sebagai `[routerLink]` RELATIF terhadap route tabnya, jadi path
   * absolut akan mengunci registry ini ke situs VENDOR — padahal slug yang sama juga dipakai
   * halaman telaah admin di bawah `/admin/vendors/:id/<slug>` (`buildProfileEditChildren()`).
   *
   * Nilainya WAJIB cocok dengan `slug` di `constants/profile-sections.ts`. Slug yang salah ketik
   * tidak menghasilkan error apa pun — build lolos, link mendarat di 404. Itulah kenapa slug-nya
   * TIDAK diulang lagi sebagai komentar di tiap entri seperti dulu: dua daftar yang lama-lama
   * berbeda adalah persis cara temuan ini lahir.
   *
   * `null` disisakan hanya sebagai pertahanan tipe; per 23 September 2026 ke-14 panel sudah punya
   * halaman.
   */
  route: string | null;
  /** `null` = tidak di-fetch (panel belum berkontrak). */
  endpoint: string | null;
  normalize: ((raw: RawEntry) => BaseEntry) | null;
  group: PanelGroup;
  /**
   * HANYA boleh membaca `ctx.vendor`. Dipanggil SEBELUM data ada, untuk menentukan apa yang perlu
   * di-fetch — menyentuh `ctx.data` di sini adalah dependensi melingkar.
   */
  required: (ctx: Ctx) => boolean;
  validate: (ctx: Ctx) => boolean;
  missing?: (ctx: Ctx) => MissingHint[];
  /**
   * Rincian syarat panel ini, satu butir per syarat — dirender sebagai checklist di atas halaman
   * section (`<app-section-checklist>`).
   *
   * ⚠️ WAJIB setara dengan `validate`: semua butir `done` ⇔ `validate(ctx) === true`. Aturan yang
   * diubah di satu tempat saja membuat checklist hijau semua sementara dashboard masih ⚠️ (atau
   * sebaliknya) — tanpa error apa pun.
   */
  checklist?: (ctx: Ctx) => ChecklistItem[];
  /**
   * Key `GET /api/lookup` yang memuat NAMA untuk butir `IDENT` panel ini (`{ ident_name, name }`)
   * — daftar yang sama dengan opsi "Tipe" di modal section-nya. Nama dokumen tidak pernah ditulis
   * di FE: yang ada di sini hanya ident dari `panel-rules.ts`.
   */
  identLookupKey?: string;
}

const identItems = (
  required: readonly string[],
  present: readonly (string | null)[],
): ChecklistItem[] =>
  required.map((value) => ({ kind: 'IDENT', value, done: present.includes(value) }));

const exactlyOne = <T>(rows: readonly T[], p: (r: T) => boolean): boolean =>
  rows.filter(p).length === 1;

/**
 * Identitas pengurus — definisi **KETAT**: nomor DAN file scan harus ada.
 *
 * Kode referensi lama tidak konsisten (satu tempat cek nomor, tempat lain cek file). Satu
 * definisi, dipakai di mana-mana (KONSEP §6.3). Kewarganegaraan kosong = belum lengkap.
 */
export const hasValidIdentity = (p: BoardEntry): boolean => {
  if (p.nationalityIdent === 'WNI') return p.ktpNumber !== null && p.ktpFile !== null;
  if (p.nationalityIdent === 'WNA') return p.kitasNumber !== null && p.kitasFile !== null;
  return false;
};

/**
 * Panel yang belum punya endpoint & aturan — ditampilkan, tidak divalidasi (KONSEP §10).
 *
 * `route` tetap WAJIB diisi: "belum berkontrak" hanya berarti kelengkapannya belum bisa DIHITUNG,
 * bukan bahwa halamannya tidak ada. Ke-15 halaman section sudah jadi
 * (`constants/profile-sections.ts`), dan baris yang tidak bisa diklik memaksa user mengingat
 * sendiri bagian mana yang kurang sebelum pindah halaman.
 */
const undefinedPanel = (
  key: PanelKey,
  labelKey: string,
  group: PanelGroup,
  route: string,
): PanelDef => ({
  key,
  labelKey,
  messageKey: null,
  route,
  endpoint: null,
  normalize: null,
  group,
  required: () => false,
  // `false`, bukan `true`: kalau suatu hari `required` diubah tanpa validator ditulis, panelnya
  // gagal — bukan lolos diam-diam.
  validate: () => false,
});

export const PANELS: readonly PanelDef[] = [
  {
    // 1 — slug `organizations`, BUKAN `general_data`: slug & label dua section pertama memang
    //     BERSILANGAN (lihat `constants/profile-sections.ts`). `general_data` berlabel "Profil
    //     Perusahaan" dan tidak punya `PanelKey` sama sekali — ia section ke-15 yang tidak ikut
    //     dihitung kelengkapan.
    key: 'GENERAL_DATA',
    labelKey: 'vendorProfile.panel.generalData',
    messageKey: 'vendorProfile.completeness.message.generalData',
    route: 'organizations',
    endpoint: 'vendor/registration/profile_offices',
    normalize: normalizeOffice,
    group: 'ADMINISTRASI',
    required: () => true,
    // `every`, bukan `some`: satu kantor yang kategorinya kosong menggagalkan panel.
    // `is_primary` sengaja TIDAK divalidasi — keputusan final KONSEP §6.1.
    validate: ({ data }) =>
      data.GENERAL_DATA.length > 0 &&
      data.GENERAL_DATA.every((o) => o.businessEntity !== null && o.companyCategory !== null),
    checklist: ({ data }) => {
      const rows = data.GENERAL_DATA;
      return [
        { kind: 'RULE', labelKey: 'vendorProfile.checklist.rule.office', done: rows.length > 0 },
        {
          kind: 'RULE',
          labelKey: 'vendorProfile.checklist.rule.officeCategory',
          done:
            rows.length > 0 &&
            rows.every((o) => o.businessEntity !== null && o.companyCategory !== null),
        },
      ];
    },
  },
  {
    // 2
    key: 'LEGAL_BASIS',
    labelKey: 'vendorProfile.panel.legalBasis',
    messageKey: 'vendorProfile.completeness.message.legalBasis',
    route: 'legal_bases',
    endpoint: 'vendor/registration/profile_deeds',
    identLookupKey: 'deed_types',
    normalize: normalizeDeed,
    group: 'ADMINISTRASI',
    required: () => true,
    validate: ({ vendor, data }) => {
      const rows = data.LEGAL_BASIS;
      if (rows.length === 0) return false;
      const idents = rows.map((d) => d.deedTypeIdent);
      return (
        requiredDeedTypes(vendor).every((t) => idents.includes(t)) &&
        rows.every((d) => d.files.length > 0)
      );
    },
    missing: ({ vendor, data }) => {
      const rows = data.LEGAL_BASIS;
      const idents = rows.map((d) => d.deedTypeIdent);
      const noFile = rows.filter((d) => d.files.length === 0).length;
      return [
        ...requiredDeedTypes(vendor)
          .filter((t) => !idents.includes(t))
          .map((value): MissingHint => ({ kind: 'IDENT', value })),
        ...(noFile > 0 ? [{ kind: 'NO_ATTACHMENT' as const, count: noFile }] : []),
      ];
    },
    checklist: ({ vendor, data }) => {
      const rows = data.LEGAL_BASIS;
      return [
        ...identItems(
          requiredDeedTypes(vendor),
          rows.map((d) => d.deedTypeIdent),
        ),
        {
          kind: 'RULE',
          labelKey: 'vendorProfile.checklist.rule.deedAttachment',
          done: rows.length > 0 && rows.every((d) => d.files.length > 0),
        },
      ];
    },
  },
  // 3 — aturannya nanti pola numeric aggregate: Math.round(total * 100) / 100 === 100 (KONSEP §10).
  undefinedPanel('SHAREHOLDER', 'vendorProfile.panel.shareholder', 'ADMINISTRASI', 'shareholders'),
  {
    // 4
    key: 'BOARD',
    labelKey: 'vendorProfile.panel.board',
    messageKey: 'vendorProfile.completeness.message.board',
    route: 'boards',
    endpoint: 'vendor/registration/profile_bod_bocs',
    identLookupKey: 'legal_org_board_types',
    normalize: normalizeBoard,
    group: 'ADMINISTRASI',
    required: () => true,
    validate: ({ vendor, data }) => {
      const rows = data.BOARD;
      if (rows.length === 0) return false;

      // (1) TEPAT SATU pimpinan: nol = tidak ada penanggung jawab, dua = ambigu.
      const heads = rows.filter((p) => p.isCompanyHead);
      if (heads.length !== 1) return false;

      const head = heads[0];
      if (head.boardTypeIdent !== 'BOD') return false; // (2) komisaris tidak boleh jadi pimpinan
      if (!hasValidIdentity(head)) return false; // (4) WNI → KTP, WNA → KITAS

      // (3) dijamin oleh (1)+(2), tapi ditulis eksplisit agar aturan tetap benar bila (2) diubah.
      const types = rows.map((p) => p.boardTypeIdent);
      return requiredBoardTypes(vendor).every((t) => types.includes(t));
    },
    checklist: ({ vendor, data }) => {
      const rows = data.BOARD;
      const heads = rows.filter((p) => p.isCompanyHead);
      const head = heads.length === 1 ? heads[0] : null;
      return [
        ...identItems(
          requiredBoardTypes(vendor),
          rows.map((p) => p.boardTypeIdent),
        ),
        {
          kind: 'RULE',
          labelKey: 'vendorProfile.checklist.rule.boardHead',
          done: head !== null && head.boardTypeIdent === 'BOD',
        },
        {
          kind: 'RULE',
          labelKey: 'vendorProfile.checklist.rule.boardHeadIdentity',
          done: head !== null && hasValidIdentity(head),
        },
      ];
    },
  },
  {
    // 5
    key: 'BUSINESS_PERMIT',
    labelKey: 'vendorProfile.panel.businessPermit',
    messageKey: 'vendorProfile.completeness.message.businessPermit',
    route: 'business_permits',
    endpoint: 'vendor/registration/profile_business_permits',
    identLookupKey: 'business_permit_types',
    normalize: normalizeBusinessPermit,
    group: 'ADMINISTRASI',
    // Panelnya sendiri kondisional: badan usaha sederhana tidak wajib punya izin usaha.
    required: ({ vendor }) => !isSimpleOrg(vendor),
    // Pola required-set: loop atas daftar SYARAT, bukan atas data. Banyaknya dokumen tidak
    // berarti apa-apa; yang dihitung adalah apakah tiap jenis wajib punya padanan.
    validate: ({ vendor, data }) => {
      const idents = data.BUSINESS_PERMIT.map((p) => p.credTypeIdent);
      return requiredBusinessPermits(vendor).every((t) => idents.includes(t));
    },
    missing: ({ vendor, data }) => {
      const idents = data.BUSINESS_PERMIT.map((p) => p.credTypeIdent);
      return requiredBusinessPermits(vendor)
        .filter((t) => !idents.includes(t))
        .map((value) => ({ kind: 'IDENT', value }));
    },
    checklist: ({ vendor, data }) =>
      identItems(
        requiredBusinessPermits(vendor),
        data.BUSINESS_PERMIT.map((p) => p.credTypeIdent),
      ),
  },
  {
    // 6
    key: 'PERSON_IN_CHARGE',
    labelKey: 'vendorProfile.panel.personInCharge',
    messageKey: 'vendorProfile.completeness.message.personInCharge',
    route: 'person_in_charges',
    endpoint: 'vendor/registration/profile_pics',
    normalize: normalizePic,
    group: 'ADMINISTRASI',
    required: () => true,
    // `=== 1`, BUKAN `>= 1`: nol PIC utama = tidak ada yang bisa dihubungi, dua = ambigu.
    validate: ({ data }) => exactlyOne(data.PERSON_IN_CHARGE, (p) => p.isPrimary),
    checklist: ({ data }) => [
      {
        kind: 'RULE',
        labelKey: 'vendorProfile.checklist.rule.primaryPic',
        done: exactlyOne(data.PERSON_IN_CHARGE, (p) => p.isPrimary),
      },
    ],
  },
  // 7
  undefinedPanel('TOOLS', 'vendorProfile.panel.tools', 'KOMPETENSI', 'tools'),
  // 8
  undefinedPanel('EXPERTS', 'vendorProfile.panel.experts', 'KOMPETENSI', 'experts'),
  // 9
  undefinedPanel(
    'CERTIFICATION',
    'vendorProfile.panel.certification',
    'KOMPETENSI',
    'certifications',
  ),
  {
    // 10
    key: 'COMPETENCY',
    labelKey: 'vendorProfile.panel.competency',
    messageKey: 'vendorProfile.completeness.message.competency',
    route: 'competencies',
    endpoint: 'vendor/registration/profile_competencies',
    normalize: normalizeCompetency,
    group: 'KOMPETENSI',
    required: () => true,
    validate: ({ data }) => {
      const rows = data.COMPETENCY;
      return rows.length >= MIN_ENTRY && rows.every((c) => c.files.length > 0);
    },
    missing: ({ data }) => {
      const noFile = data.COMPETENCY.filter((c) => c.files.length === 0).length;
      return noFile > 0 ? [{ kind: 'NO_ATTACHMENT', count: noFile }] : [];
    },
    checklist: ({ data }) => {
      const rows = data.COMPETENCY;
      return [
        {
          kind: 'RULE',
          labelKey: 'vendorProfile.checklist.rule.competency',
          done: rows.length >= MIN_ENTRY,
        },
        {
          kind: 'RULE',
          labelKey: 'vendorProfile.checklist.rule.competencyAttachment',
          done: rows.length >= MIN_ENTRY && rows.every((c) => c.files.length > 0),
        },
      ];
    },
  },
  // 11 — slug `portfolios`; menunya berlabel "Pengalaman Kerja" (`panel.experience`).
  undefinedPanel('EXPERIENCE', 'vendorProfile.panel.experience', 'KOMPETENSI', 'portfolios'),
  // 12
  undefinedPanel('BANK_ACCOUNT', 'vendorProfile.panel.bankAccount', 'KEUANGAN', 'bank_accounts'),
  // 13
  undefinedPanel(
    'FINANCIAL_STATEMENT',
    'vendorProfile.panel.financialStatement',
    'KEUANGAN',
    'financial_statements',
  ),
  {
    // 14
    key: 'TAX_DOCUMENT',
    labelKey: 'vendorProfile.panel.taxDocument',
    messageKey: 'vendorProfile.completeness.message.taxDocument',
    route: 'tax_documents',
    endpoint: 'vendor/registration/profile_tax_docs',
    identLookupKey: 'tax_doc_types',
    normalize: normalizeTaxDoc,
    group: 'KEUANGAN',
    required: () => true,
    validate: ({ vendor, data }) => {
      const idents = data.TAX_DOCUMENT.map((t) => t.taxDocTypeIdent);
      return requiredTaxTypes(vendor).every((t) => idents.includes(t));
    },
    missing: ({ vendor, data }) => {
      const idents = data.TAX_DOCUMENT.map((t) => t.taxDocTypeIdent);
      return requiredTaxTypes(vendor)
        .filter((t) => !idents.includes(t))
        .map((value) => ({ kind: 'IDENT', value }));
    },
    checklist: ({ vendor, data }) =>
      identItems(
        requiredTaxTypes(vendor),
        data.TAX_DOCUMENT.map((t) => t.taxDocTypeIdent),
      ),
  },
];

export const PANEL_BY_KEY: ReadonlyMap<PanelKey, PanelDef> = new Map(PANELS.map((p) => [p.key, p]));

/**
 * Semua key → `[]`. Menjamin invarian **`data[KEY]` selalu array**, jadi tidak ada validator yang
 * perlu `?.` atau null-guard.
 */
export const EMPTY_PANEL_DATA: PanelData = Object.fromEntries(
  PANELS.map((p) => [p.key, [] as readonly BaseEntry[]]),
) as PanelData;
